import React, { useState } from 'react';
import { Row, Col, Card, Select, Spin, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
  CardTitleText,
  MenuItemText,
  TableColumnTitle,
} from '../../../../../../../shared/globalStyling/styledText';
import TransactionPublisherTable from './TransactionPublisherTable';
import { colors } from '../../../../../../../../core/utils/constants/listOfColors';
import TransactionPublisherRingChart from './TransactionPublisherRingChart';

const { Option } = Select;

const Style = {
  campaignCardStyle: {
    margin: 5,
    padding: 2,
    height: 340,
    backgroundColor: '#fff',
  },
  campaignCardStylePublishers: {
    backgroundColor: '#fff',
  },
  keyColors: {
    spent: '#1665d8',
    remaining: '#34aa44',
    budget: '#030303',
    total: '#bec0c3',
    value: '#99999',
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  xs: {
    span: 24,
    offset: 0,
  },
  sm: {
    span: 24,
    offset: 0,
  },
  md: {
    span: 16,
    offset: 0,
  },
  lg: {
    span: 16,
    offset: 0,
  },
  xl: {
    span: 16,
    offset: 0,
  },
  xsPie: {
    span: 24,
    offset: 0,
  },
  smPie: {
    span: 24,
    offset: 0,
  },
  mdPie: {
    span: 8,
    offset: 0,
  },
  lgPie: {
    span: 8,
    offset: 0,
  },
  xlPie: {
    span: 8,
    offset: 0,
  },
};

const {
  xs,
  sm,
  md,
  lg,
  xl,
  xsPie,
  smPie,
  mdPie,
  lgPie,
  xlPie,
  campaignCardStylePublishers,
  campaignCardStyle,
} = Style;

const Extra = ({ setPublisherDimension }) => {
  const options = [
    {
      value: 'impressions',
      text: 'Impressions',
    },
    {
      value: 'totalTransactions',
      text: 'Transactions',
    },
  ];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '4px' }}>
      <div style={{ whiteSpace: 'nowrap' }}>
        <CardTitleText text={'Breakdown'} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', minWidth: 0, gap: '4px' }}>
        <span style={{ whiteSpace: 'nowrap' }}>
          <TableColumnTitle text={'Show By:'} />
        </span>
        <Select
          style={{ minWidth: '80px', maxWidth: '140px' }}
          defaultValue={options[0].value}
          onChange={(value) => {
            setPublisherDimension(value);
          }}
        >
          {options.map((option, index) => {
            return (
              <Option key={index} value={option.value}>
                <MenuItemText text={option.text} />
              </Option>
            );
          })}
        </Select>
      </div>
    </div>
  );
};

const TransactionPublisherDetails = ({ publishers = [], loading = false, byDateLoading = false }) => {
  const [publisherDimension, setPublisherDimension] = useState('impressions');

  // Consolidate publishers with the same name
  const consolidatedPublishers = publishers.reduce((acc, publisher) => {
    const name = publisher.name || publisher.media || 'Unknown';
    const existingPublisher = acc.find((p) => p.name === name);

    if (existingPublisher) {
      // Aggregate metrics for publishers with the same name
      existingPublisher.impressions += publisher.impressions || 0;
      existingPublisher.transactions += publisher.transactions || 0;
      // Keep the first media if available
      if (!existingPublisher.media && publisher.media) {
        existingPublisher.media = publisher.media;
      }
    } else {
      // Add new publisher entry
      acc.push({
        ...publisher,
        name: name,
        impressions: publisher.impressions || 0,
        transactions: publisher.transactions || 0,
      });
    }

    return acc;
  }, []);

  // Sort by impression count (descending)
  const sortedPublishers = consolidatedPublishers.sort((a, b) => b.impressions - a.impressions);

  // Assign colors and value field to each consolidated publisher based on their index
  const publishersWithColors = sortedPublishers.map((publisher, index) => ({
    ...publisher,
    color: colors[index % colors.length],
    value: publisher.name || publisher.media || `publisher-${index}`,
    totalTransactions: publisher.transactions,
  }));

  // Custom title with loading indicator
  const PublishersTitle = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
      <CardTitleText text={'Publishers'} />
      {byDateLoading && (
        <Tooltip title="Loading by-date data for filtering. Date range filters will be available once complete.">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
        </Tooltip>
      )}
    </div>
  );

  return (
    <Row gutter={35}>
      <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl}>
        <Card
          style={campaignCardStylePublishers}
          headStyle={{ borderWidth: '3px', height: '66px' }}
          bodyStyle={{ padding: 0 }}
          title={<PublishersTitle />}
        >
          <TransactionPublisherTable publisherData={publishersWithColors} />
        </Card>
      </Col>
      <Col xs={xsPie} sm={smPie} md={mdPie} lg={lgPie} xl={xlPie}>
        <Card
          style={campaignCardStylePublishers}
          bodyStyle={{ padding: 0, height: '100%' }}
          headStyle={{ borderWidth: '2px', minHeight: '66px', height: 'auto' }}
          title={<Extra setPublisherDimension={setPublisherDimension} />}
        >
          <TransactionPublisherRingChart
            data={publishersWithColors}
            height={campaignCardStyle.height}
            selectedPublisherDimension={publisherDimension}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default TransactionPublisherDetails;
